<template>
    <div class="video-form">
        <h3>{{ mode === 'edit' ? (ta ? 'காணொளியைத் திருத்து' : 'Edit Video') : (ta ? 'காணொளி தகவல்' : 'Video Information') }}</h3>
        
        <!-- Show video ID only in edit mode -->
        <div class="form-group" v-if="mode === 'edit'">
            <label>{{ ta ? 'காணொளி அடையாளம்' : 'Video ID' }}</label>
            <p class="video-id">{{ video.id }}</p>
        </div>

        <div class="form-group" v-if="mode === 'edit'">
            <label>{{ ta ? 'காணொளி கோப்பை மாற்றவும்' : 'Replace Video File' }}</label>
            <div class="replace-video-panel">
                <input
                    ref="replaceVideoInput"
                    type="file"
                    accept="video/mp4,video/*"
                    hidden
                    @change="handleVideoFileChange"
                />
                <button
                    type="button"
                    class="replace-video-button"
                    :disabled="isUploadingVideo || isPreparingVideo"
                    @click="openVideoPicker"
                >
                    <vue-feather
                        :type="isUploadingVideo || isPreparingVideo ? 'loader' : 'upload-cloud'"
                        :animation="isUploadingVideo || isPreparingVideo ? 'spin' : undefined"
                        size="18"
                        stroke-width="2"
                    ></vue-feather>
                    <span>{{ uploadButtonLabel }}</span>
                </button>

                <div class="replace-video-copy">
                    <strong>{{ ta ? 'இது தற்போதைய காணொளியை மீண்டும் செயலாக்கும்.' : 'This will reprocess the current video.' }}</strong>
                    <span>{{ ta ? 'அதே காணொளி அடையாளம் பயன்படுத்தப்படும், அதனால் ஏற்கனவே உள்ள இணைப்புகள் தொடரும்.' : 'The same video ID will be used, so existing links stay the same.' }}</span>
                </div>

                <div v-if="isUploadingVideo" class="replace-upload-progress">
                    <div class="replace-progress-track">
                        <div class="replace-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                    <span>{{ parseInt(uploadProgress) }}%</span>
                </div>

                <div v-if="uploadMessage" class="replace-video-message" :class="{ success: uploadStatus === 'success' }">
                    <vue-feather :type="uploadStatus === 'success' ? 'check-circle' : 'info'" size="16" stroke-width="2"></vue-feather>
                    <span>{{ uploadMessage }}</span>
                </div>
            </div>
        </div>

        <!-- Video Image Selection -->
        <div class="form-group">
            <label>{{ ta ? 'காணொளி சிறுபடம்' : 'Video Thumbnail' }}</label>
            <div class="image-selection-container">
                <div class="image-grid">
                    <div 
                        v-for="imageIndex in 6" 
                        :key="imageIndex - 1"
                        class="image-option"
                        :class="{ 'selected': selectedImageIndex === (imageIndex - 1).toString() }"
                        @click="selectImage((imageIndex - 1).toString())"
                    >
                        <div 
                            class="image-preview" 
                            :style="`background-image: url('${getImageURL(imageIndex - 1)}')`"
                        ></div>
                        <div class="image-label">{{ ta ? 'படம்' : 'Image' }} {{ imageIndex - 1 }}</div>
                    </div>
                </div>
                <div class="image-info">
                    <p>{{ ta ? 'உங்கள் காணொளிக்கான சிறுபடத்தைத் தேர்ந்தெடுக்கவும்.' : 'Select one of the available thumbnail images for your video.' }}</p>
                    <p v-if="mode === 'edit' && !hasAllImages" class="warning">
                        <vue-feather type="alert-triangle" size="16" stroke-width="2"></vue-feather>
                        {{ ta ? 'சில சிறுபடங்கள் இன்னும் கிடைக்கவில்லை. காணொளி செயலாக்கத்தின் போது அவை உருவாக்கப்படும்.' : 'Some thumbnail images may not be available yet. They will be generated during video processing.' }}
                    </p>
                </div>
            </div>
        </div>

        <div class="form-group">
            <label>{{ ta ? 'தலைப்பு (தமிழ்)' : 'Title (Tamil)' }}</label>
            <input
                v-model="localVideo.titleTA"
                type="text"
                :placeholder="ta ? 'தமிழ் தலைப்பை உள்ளிடவும்' : 'Enter Tamil title'"
                @input="updateVideo"
            />
        </div>

        <div class="form-group">
            <label>{{ ta ? 'தலைப்பு (ஆங்கிலம்)' : 'Title (English)' }}</label>
            <input
                v-model="localVideo.titleEN"
                type="text"
                :placeholder="ta ? 'ஆங்கில தலைப்பை உள்ளிடவும்' : 'Enter English title'"
                @input="updateVideo"
            />
        </div>

        <div class="form-group">
            <label>{{ ta ? 'விளக்கம் (தமிழ்)' : 'Description (Tamil)' }}</label>
            <textarea
                v-model="localVideo.descriptionTA"
                :placeholder="ta ? 'தமிழ் விளக்கத்தை உள்ளிடவும்' : 'Enter Tamil description'"
                @input="updateVideo"
            ></textarea>
        </div>

        <div class="form-group">
            <label>{{ ta ? 'விளக்கம் (ஆங்கிலம்)' : 'Description (English)' }}</label>
            <textarea
                v-model="localVideo.descriptionEN"
                :placeholder="ta ? 'ஆங்கில விளக்கத்தை உள்ளிடவும்' : 'Enter English description'"
                @input="updateVideo"
            ></textarea>
        </div>

        <div class="form-group">
            <label>{{ ta ? 'முக்கிய தேதி (விரும்பினால்)' : 'Significant Date (Optional)' }}</label>
            <div class="date-input-container">
                <input 
                    v-model="localVideo.significantDate" 
                    type="date" 
                    class="date-input"
                    @input="updateVideo"
                />
                <button 
                    v-if="localVideo.significantDate" 
                    @click="clearSignificantDate" 
                    type="button"
                    class="clear-date-button"
                    title="Clear date"
                >
                    <vue-feather type="x" size="16" stroke-width="2"></vue-feather>
                </button>
            </div>
            <small class="form-help">{{ ta ? 'இந்த காணொளி தொடர்பான முக்கிய தேதியை அமைக்கவும் (எ.கா., நிகழ்வு தேதி, நினைவு நாள் போன்றவை)' : 'Set a significant date related to this video (e.g., event date, anniversary, etc.)' }}</small>
        </div>

        <div class="form-group">
            <label>{{ ta ? 'முதன்மை வகை' : 'Primary Category' }}</label>
            <select
                v-model="localPrimaryCategory"
                @change="updatePrimaryCategory"
            >
                <option :value="null">{{ ta ? 'வகையைத் தேர்ந்தெடுக்கவும்' : 'Select a category' }}</option>
                <option
                    v-for="category in primaryCategories"
                    :key="category.id"
                    :value="category"
                >
                    {{ category.nameTA }} ({{ category.nameEN }})
                </option>
            </select>
        </div>

        <div 
            class="form-group" 
            v-if="localPrimaryCategory?.children?.items?.length"
        >
            <label>{{ ta ? 'இரண்டாம் வகை' : 'Secondary Category' }}</label>
            <select 
                v-model="localSecondaryCategory" 
                @change="updateSecondaryCategory"
            >
                <option :value="null">{{ ta ? 'துணை வகையைத் தேர்ந்தெடுக்கவும்' : 'Select a subcategory' }}</option>
                <option 
                    v-for="subcategory in localPrimaryCategory.children.items" 
                    :key="subcategory.id" 
                    :value="subcategory"
                >
                    {{ subcategory.nameTA }} ({{ subcategory.nameEN }})
                </option>
            </select>
        </div>

        <div class="form-group">
            <label>{{ ta ? 'குறிச்சொற்கள்' : 'Tags' }}</label>
            <div class="tag-input-container">
                <input 
                    v-model="tagInput" 
                    type="text" 
                    :placeholder="ta ? 'குறிச்சொல்லை உள்ளிட்டு Enter அழுத்தவும்' : 'Type tag name and press Enter'"
                    @keydown.enter.prevent="addTag"
                    @input="filterTags"
                    class="tag-input"
                />
                <div v-if="filteredTags.length > 0 && tagInput" class="tag-suggestions">
                    <div 
                        v-for="tag in filteredTags" 
                        :key="tag.id"
                        @click="selectTag(tag)"
                        class="tag-suggestion"
                    >
                        {{ tag.label }}
                    </div>
                </div>
            </div>
            <div class="selected-tags">
                <div 
                    v-for="tag in localTags" 
                    :key="tag.id"
                    class="tag-item"
                >
                    <span class="tag-name">{{ tag.label }}</span>
                    <button 
                        @click="removeTag(tag)"
                        class="remove-tag"
                        type="button"
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>

        <div class="form-actions">
            <!-- Error Message -->
            <div v-if="errorMessage" class="error-message">
                <vue-feather type="alert-circle" size="20" stroke-width="2"></vue-feather>
                <span>{{ errorMessage }}</span>
                <button @click="clearError" class="error-close">
                    <vue-feather type="x" size="16" stroke-width="2"></vue-feather>
                </button>
            </div>
            
            <!-- Success messages -->
            <div v-if="isCreated" class="success-message">{{ ta ? 'காணொளி உருவாக்கப்பட்டது!' : 'Video Created!' }}</div>
            <div v-if="isUpdated" class="success-message">{{ ta ? 'காணொளி புதுப்பிக்கப்பட்டது!' : 'Video Updated!' }}</div>
            <div v-if="isDeleted" class="success-message">{{ ta ? 'காணொளி நீக்கப்பட்டது!' : 'Video Deleted!' }}</div>
            
            <!-- Action buttons -->
            <div class="button-group">
                <button
                    v-if="mode === 'create'"
                    class="btn btn-primary"
                    @click="handleSave"
                    :disabled="isCreating || isInvalidForm"
                >
                    <span>{{ isCreated ? (ta ? 'காணொளி உருவாக்கப்பட்டது!' : 'Video Created!') : (ta ? 'காணொளியை உருவாக்கு' : 'Create Video') }}</span>
                    <vue-feather :type="isCreated ? 'check' : 'chevron-right'" size="18"></vue-feather>
                </button>
                
                <button 
                    v-if="mode === 'edit'"
                    class="btn btn-primary" 
                    @click="handleSave" 
                    :disabled="isUpdating || isInvalidForm"
                >
                    {{ isUpdated ? (ta ? 'காணொளி புதுப்பிக்கப்பட்டது!' : 'Video Updated!') : (ta ? 'காணொளியைப் புதுப்பி' : 'Update Video') }}
                </button>
                
                <button 
                    v-if="mode === 'edit' && !isDeleted"
                    class="btn btn-danger" 
                    @click="handleDelete" 
                    :disabled="isDeleting"
                >
                    {{ isDeleted ? (ta ? 'காணொளி நீக்கப்பட்டது!' : 'Video Deleted!') : (ta ? 'காணொளியை நீக்கு' : 'Delete Video') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'VideoForm',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    props: {
        mode: {
            type: String,
            default: 'create',
            validator: value => ['create', 'edit'].includes(value)
        },
        video: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            required: true
        },
        tags: {
            type: Array,
            default: () => []
        },
        availableTags: {
            type: Array,
            default: () => []
        },
        primaryCategory: {
            type: Object,
            default: null
        },
        secondaryCategory: {
            type: Object,
            default: null
        },
        isCreating: {
            type: Boolean,
            default: false
        },
        isCreated: {
            type: Boolean,
            default: false
        },
        isUpdating: {
            type: Boolean,
            default: false
        },
        isUpdated: {
            type: Boolean,
            default: false
        },
        isDeleting: {
            type: Boolean,
            default: false
        },
        isDeleted: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: ''
        },
        isUploadingVideo: {
            type: Boolean,
            default: false
        },
        isPreparingVideo: {
            type: Boolean,
            default: false
        },
        uploadProgress: {
            type: Number,
            default: 0
        },
        uploadStatus: {
            type: String,
            default: ''
        },
        uploadMessage: {
            type: String,
            default: ''
        },
        thumbnailRefreshKey: {
            type: [String, Number],
            default: ''
        }
    },
    data() {
        return {
            localVideo: { ...this.video },
            localPrimaryCategory: this.primaryCategory,
            localSecondaryCategory: this.secondaryCategory,
            localTags: [...this.tags],
            tagInput: '',
            filteredTags: [],
            selectedImageIndex: this.video.imageName || '0'
        }
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        ta() {
            return this.currentLanguage === 'TA';
        },
        // Only show main categories (not subcategories) in primary dropdown
        primaryCategories() {
            return this.categories.filter(cat => !cat.categoryModelChildrenId);
        },
        isInvalidForm() {
            return !this.localVideo.titleTA ||
                   !this.localVideo.titleEN ||
                   !this.localVideo.descriptionTA ||
                   !this.localVideo.descriptionEN;
        },
        imageURL() {
            if (this.mode === 'edit' && this.localVideo.id) {
                return `https://cdn1.eelam.tv/public/thumbs/${this.localVideo.id}_${this.selectedImageIndex}.jpeg`;
            }
            return null;
        },
        uploadButtonLabel() {
            if (this.isUploadingVideo) {
                return this.ta ? 'பதிவேற்றப்படுகிறது...' : 'Uploading...';
            }
            if (this.isPreparingVideo) {
                return this.ta ? 'செயலாக்கப்படுகிறது...' : 'Processing...';
            }
            return this.ta ? 'காணொளியை பதிவேற்றவும்' : 'Upload replacement video';
        },
        hasAllImages() {
            // For edit mode, check if we have the video ID to determine if images might be available
            return this.mode === 'edit' && this.localVideo.id;
        }
    },
    watch: {
        video: {
            handler(newVal) {
                this.localVideo = { ...newVal };
            },
            deep: true
        },
        tags: {
            handler(newVal) {
                this.localTags = [...newVal];
            },
            deep: true
        },
        primaryCategory: {
            handler(newVal) {
                this.localPrimaryCategory = newVal;
            }
        },
        secondaryCategory: {
            handler(newVal) {
                this.localSecondaryCategory = newVal;
            }
        }
    },
    methods: {
        updateVideo() {
            this.$emit('update:video', this.localVideo);
        },
        updatePrimaryCategory() {
            this.$emit('update:primary-category', this.localPrimaryCategory);
        },
        updateSecondaryCategory() {
            this.$emit('update:secondary-category', this.localSecondaryCategory);
        },
        addTag() {
            if (this.tagInput.trim()) {
                // Check if tag already exists
                const existingTag = this.localTags.find(tag => 
                    tag.label.toLowerCase() === this.tagInput.trim().toLowerCase()
                );
                
                if (!existingTag) {
                    // Create new tag object (will be saved when form is submitted)
                    const newTag = {
                        id: `temp-${Date.now()}`,
                        label: this.tagInput.trim(),
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                    };
                    
                    this.localTags.push(newTag);
                    this.$emit('update:tags', this.localTags);
                }
                
                this.tagInput = '';
                this.filteredTags = [];
            }
        },
        removeTag(tag) {
            const index = this.localTags.findIndex(t => t.id === tag.id);
            if (index > -1) {
                this.localTags.splice(index, 1);
                this.$emit('update:tags', this.localTags);
            }
        },
        selectTag(tag) {
            const existingTag = this.localTags.find(t => t.id === tag.id);
            if (!existingTag) {
                this.localTags.push(tag);
                this.$emit('update:tags', this.localTags);
            }
            this.tagInput = '';
            this.filteredTags = [];
        },
        filterTags() {
            if (this.tagInput.trim()) {
                // Filter available tags that match input and aren't already selected
                this.filteredTags = this.availableTags.filter(tag => {
                    const isNotSelected = !this.localTags.find(t => t.id === tag.id);
                    const matchesInput = tag.label.toLowerCase().includes(this.tagInput.toLowerCase());
                    return isNotSelected && matchesInput;
                }).slice(0, 5); // Limit to 5 suggestions
            } else {
                this.filteredTags = [];
            }
        },
        handleSave() {
            if (!this.isInvalidForm) {
                this.$emit('save');
            }
        },
        handleDelete() {
            const videoTitle = this.localVideo.titleEN || this.localVideo.titleTA || this.localVideo.id;
            const message = this.ta
                ? `இந்த காணொளியை நீக்க வேண்டுமா?\n\n${videoTitle}\n\nஇந்த செயலை மாற்ற முடியாது.`
                : `Delete this video?\n\n${videoTitle}\n\nThis action cannot be undone.`;

            if (window.confirm(message)) {
                this.$emit('delete');
            }
        },
        openVideoPicker() {
            if (!this.isUploadingVideo && !this.isPreparingVideo) {
                this.$refs.replaceVideoInput.click();
            }
        },
        handleVideoFileChange(event) {
            const file = event.target.files?.[0];
            if (file) {
                this.$emit('replace-video', file);
            }
            event.target.value = '';
        },
        clearError() {
            this.$emit('clear-error');
        },
        clearSignificantDate() {
            this.localVideo.significantDate = null;
            this.updateVideo();
        },
        selectImage(imageIndex) {
            this.selectedImageIndex = imageIndex;
            this.localVideo.imageName = imageIndex;
            // Selecting a generated thumbnail replaces the legacy one; clear
            // thumbnailUrl since display components prefer it over imageName.
            if (String(imageIndex) !== '0') {
                this.localVideo.thumbnailUrl = null;
            }
            this.updateVideo();
        },
        getImageURL(imageIndex) {
            if (this.mode === 'edit' && this.localVideo.id) {
                // For legacy videos with thumbnailUrl, use that for image 0
                if (imageIndex === 0 && this.localVideo.thumbnailUrl) {
                    return this.withThumbnailRefreshKey(`https://cdn.eelam.tv/${this.localVideo.thumbnailUrl}`);
                }
                return this.withThumbnailRefreshKey(`https://cdn1.eelam.tv/public/thumbs/${this.localVideo.id}_${imageIndex}.jpeg`);
            }
            // For create mode, show placeholder or default image
            return `https://via.placeholder.com/160x90/cccccc/666666?text=Image+${imageIndex}`;
        },
        withThumbnailRefreshKey(url) {
            if (!this.thumbnailRefreshKey) {
                return url;
            }
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}v=${this.thumbnailRefreshKey}`;
        }
    }
}
</script>

<style scoped>
.video-form {
    max-width: 600px;
    margin: 20px auto;
    padding: 24px;
    background: var(--bg-card);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-color);
}

.video-form h3 {
    color: var(--text-secondary);
    margin-bottom: 24px;
    font-size: 24px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--text-secondary);
}

input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 14px;
    background: var(--bg-input);
    color: var(--text-secondary);
    transition: border-color 0.2s, box-shadow 0.2s;
}

input:focus, textarea:focus, select:focus {
    outline: none;
    border-color: var(--text-link-hover);
    box-shadow: 0 0 0 3px rgba(19, 125, 243, 0.1);
}

input::placeholder, textarea::placeholder {
    color: var(--text-muted);
}

textarea {
    min-height: 100px;
    resize: vertical;
}

select {
    cursor: pointer;
}

select option {
    background: var(--bg-card);
    color: var(--text-secondary);
}

.date-input-container {
    position: relative;
    display: flex;
    align-items: center;
}

.date-input {
    flex: 1;
    padding: 12px;
    padding-right: 40px;
}

.clear-date-button {
    position: absolute;
    right: 10px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s, background 0.2s;
}

.clear-date-button:hover {
    color: var(--text-secondary);
    background: var(--bg-hover);
}

.form-help {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: var(--text-muted);
    font-style: italic;
}

.video-id {
    padding: 12px;
    background: var(--bg-tertiary);
    border-radius: 8px;
    font-family: monospace;
    color: var(--text-tertiary);
    font-size: 13px;
    border: 1px solid var(--border-color);
}

.replace-video-panel {
    display: grid;
    gap: 14px;
    padding: 16px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-tertiary);
}

.replace-video-button {
    width: fit-content;
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    background: #ff1314;
    color: white;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s, opacity 0.2s;
}

.replace-video-button:hover:not(:disabled) {
    background: #e00e0f;
    transform: translateY(-1px);
}

.replace-video-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.replace-video-copy {
    display: grid;
    gap: 4px;
    color: var(--text-tertiary);
    font-size: 13px;
    line-height: 1.45;
}

.replace-video-copy strong {
    color: var(--text-secondary);
    font-size: 14px;
}

.replace-upload-progress {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: center;
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 700;
}

.replace-progress-track {
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--bg-input);
}

.replace-progress-fill {
    height: 100%;
    border-radius: inherit;
    background: #ff1314;
    transition: width 0.2s ease;
}

.replace-video-message {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-tertiary);
    font-size: 13px;
}

.replace-video-message.success {
    color: #2e7d32;
}

.image-selection-container {
    margin-top: 12px;
}

.image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 15px;
    margin-bottom: 15px;
}

.image-option {
    cursor: pointer;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.2s ease;
    background: var(--bg-card);
}

.image-option:hover {
    border-color: #ff1314;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--shadow-color);
}

.image-option.selected {
    border-color: #ff1314;
    box-shadow: 0 0 0 3px rgba(255, 19, 20, 0.2);
}

.image-preview {
    width: 100%;
    height: 90px;
    background-size: cover;
    background-position: center;
    background-color: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
}

.image-label {
    padding: 8px;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-tertiary);
    background: var(--bg-tertiary);
}

.image-option.selected .image-label {
    background: #ff1314;
    color: white;
}

.image-info {
    font-size: 13px;
    color: var(--text-tertiary);
    line-height: 1.5;
}

.image-info p {
    margin: 5px 0;
}

.warning {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #f57c00;
    font-size: 12px;
    margin-top: 10px;
    padding: 10px 12px;
    background: rgba(245, 124, 0, 0.1);
    border-radius: 6px;
    border-left: 3px solid #f57c00;
}

.video-thumbnail {
    width: 100%;
    height: 320px;
    background-size: cover;
    background-position: center;
    border-radius: 8px;
    border: 1px solid var(--border-color);
}

.tag-input-container {
    position: relative;
}

.tag-input {
    width: 100%;
}

.tag-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-dropdown);
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 8px 8px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 4px 12px var(--shadow-dropdown);
}

.tag-suggestion {
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid var(--border-light);
    color: var(--text-secondary);
    transition: background 0.15s;
}

.tag-suggestion:hover {
    background: var(--bg-hover);
}

.tag-suggestion:last-child {
    border-bottom: none;
}

.selected-tags {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-item {
    display: flex;
    align-items: center;
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.3);
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 14px;
    color: var(--text-link-hover);
}

.tag-name {
    margin-right: 8px;
}

.remove-tag {
    background: none;
    border: none;
    color: var(--text-link-hover);
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background 0.15s, color 0.15s;
}

.remove-tag:hover {
    background: var(--text-link-hover);
    color: white;
}

.form-actions {
    margin-top: 32px;
    padding-top: 20px;
    border-top: 1px solid var(--border-color);
}

.success-message {
    text-align: center;
    color: #4caf50;
    font-weight: 600;
    margin-bottom: 15px;
    padding: 12px;
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 8px;
}

.error-message {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.3);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 15px;
    color: #f44336;
    font-size: 14px;
}

.error-message svg {
    color: #f44336;
    flex-shrink: 0;
}

.error-message span {
    flex: 1;
}

.error-close {
    background: none;
    border: none;
    color: #f44336;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
}

.error-close:hover {
    background: rgba(244, 67, 54, 0.15);
}

.button-group {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.2s;
}

.btn-primary {
    background: #ff1314;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #e00e0f;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 19, 20, 0.3);
}

.btn-primary:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.btn-danger {
    background: transparent;
    color: #f44336;
    border: 1px solid #f44336;
}

.btn-danger:hover:not(:disabled) {
    background: #f44336;
    color: white;
}

.btn-danger:disabled {
    border-color: var(--text-muted);
    color: var(--text-muted);
    cursor: not-allowed;
}

/* Responsive design */
@media (max-width: 768px) {
    .video-form {
        margin: 10px;
        padding: 16px;
    }

    .date-input-container {
        flex-direction: column;
        align-items: stretch;
    }

    .clear-date-button {
        position: static;
        margin-top: 8px;
        align-self: flex-end;
        padding: 8px 12px;
        background: var(--bg-tertiary);
        border: 1px solid var(--border-color);
        border-radius: 6px;
    }

    .form-help {
        font-size: 11px;
    }

    .image-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }

    .image-preview {
        height: 70px;
    }

    .image-label {
        font-size: 11px;
        padding: 6px;
    }

    .button-group {
        flex-direction: column;
    }

    .btn {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .image-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .image-preview {
        height: 120px;
    }
}
</style> 
